/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Accordion, AccordionTab } from "primereact/accordion";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tooltip } from "primereact/tooltip";
import { Titulo, Sidebar, ArchivoPreview, accessAPI } from "../../shared";

const VACIO = { destacados: [], externos: [], carpetas: [], normas: [] };

/**
 * Los documentos que el colaborador tiene a mano en cualquier pantalla.
 *
 * La API los devuelve ya agrupados, porque de ese lado se sabe cuál es la norma
 * del proceso de la actividad abierta. Acá solo se decide cómo se muestra cada
 * grupo: destacados y externos a la vista, y el resto en acordeones para que la
 * lista no ocupe metros —en producción son decenas de documentos—.
 */
export default function SidebarDocumentos({ visible, onHide }) {
  const [datos, setDatos] = useState(VACIO);
  const [cargando, setCargando] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!visible) return;

    setCargando(true);
    // Parado en una actividad, la API marca como destacados los documentos de
    // su norma y su esquema.
    const enActividad = location.pathname.match(/^\/actividad\/(\d+)$/);
    const endpoint = enActividad
      ? `colab/documento?pasoId=${enActividad[1]}`
      : "colab/documento";

    accessAPI(
      "GET",
      endpoint,
      null,
      (respuesta) => {
        setDatos({ ...VACIO, ...respuesta });
        setCargando(false);
      },
      () => {
        setDatos(VACIO);
        setCargando(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, location.pathname]);

  const hayAlgo =
    datos.destacados.length > 0 ||
    datos.externos.length > 0 ||
    datos.normas.length > 0 ||
    datos.carpetas.some((c) => c.documentos.length > 0);

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      // Más ancho que el sidebar estándar: acá entra una lista larga y los
      // documentos se acomodan en varias columnas. PrimeFlex solo llega a
      // w-30rem, así que el ancho grande va por style.
      className="w-full"
      style={{ maxWidth: "56rem" }}
    >
      <Titulo>Documentos</Titulo>
      <Tooltip target=".tooltip-doc-info" />

      {cargando && (
        <div css={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        </div>
      )}

      {!cargando && !hayAlgo && (
        <p css={{ color: "var(--grisTextos)", textAlign: "center", marginTop: "2rem" }}>
          No hay documentos disponibles
        </p>
      )}

      {!cargando && hayAlgo && (
        <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Lo único fuera de acordeón: es lo que el colaborador necesita
              para la actividad que tiene abierta. */}
          {datos.destacados.length > 0 && (
            <div>
              <div css={{ fontWeight: 600, marginBottom: "0.5rem" }}>
                Destacados
              </div>
              <Grilla documentos={datos.destacados} />
            </div>
          )}

          {(datos.carpetas.length > 0 ||
            datos.externos.length > 0 ||
            datos.normas.length > 0) && (
            <Accordion
              multiple
              // El tema pinta el encabezado con el color de marca; acá el
              // nombre de la carpeta se lee mejor en negro.
              css={{ ".p-accordion-header-link": { color: "var(--negro)" } }}
            >
              {datos.carpetas
                .filter((carpeta) => carpeta.documentos.length > 0)
                .map((carpeta) => (
                  <AccordionTab
                    key={carpeta.id ?? "otros"}
                    header={carpeta.nombre}
                  >
                    <Grilla documentos={carpeta.documentos} />
                  </AccordionTab>
                ))}

              {datos.externos.length > 0 && (
                <AccordionTab header="Externos">
                  <Grilla documentos={datos.externos} />
                </AccordionTab>
              )}

              {datos.normas.length > 0 && (
                <AccordionTab header="Normas">
                  <Grilla documentos={datos.normas} />
                </AccordionTab>
              )}
            </Accordion>
          )}
        </div>
      )}
    </Sidebar>
  );
}

// Dos columnas cuando hay lugar. El auto-fill con un mínimo hace que en el
// sidebar angosto caiga sola a una sin necesidad de media queries.
function Grilla({ documentos }) {
  return (
    <div
      css={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(18rem, 1fr))",
        gap: "0.5rem"
      }}
    >
      {documentos.map((doc) => (
        <Documento key={doc.id} doc={doc} />
      ))}
    </div>
  );
}

function Documento({ doc }) {
  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        padding: "0.6rem 0.75rem",
        borderRadius: "8px",
        border: "1px solid var(--surface-border, #e0e0e0)"
      }}
    >
      <div css={{ flex: 1, minWidth: 0 }}>
        <div css={{ fontWeight: 600, fontSize: "0.85rem" }}>{doc.codigo}</div>

        <div css={{ fontSize: "0.85rem" }}>
          {doc.externo ? (
            // Los externos son un link a un sitio de terceros: no hay archivo
            // propio que previsualizar.
            <a
              href={doc.linkExterno || undefined}
              target="_blank"
              rel="noreferrer"
              css={{ color: "var(--principal)" }}
            >
              {doc.nombre}
            </a>
          ) : doc.archivo?.hash ? (
            // Las normas se consultan pero no se descargan: sinDescarga saca el
            // botón y la barra del visor, también al abrirlas en otra pestaña.
            <ArchivoPreview
              nombre={doc.archivo.nombredescarga}
              etiqueta={doc.nombre}
              hash={doc.archivo.hash}
              sinToolbar={doc.esNorma}
              sinDescarga={doc.esNorma}
            />
          ) : (
            <span css={{ color: "var(--grisTextos)" }}>{doc.nombre}</span>
          )}
        </div>
      </div>

      {doc.resumen && (
        <i
          className="pi pi-info-circle tooltip-doc-info"
          data-pr-tooltip={doc.resumen}
          data-pr-position="top"
          css={{ color: "var(--principal)", cursor: "pointer", flexShrink: 0 }}
        />
      )}
    </div>
  );
}
